import {
    calendarNotificationsBarDeleteCalendarGroup,
    calendarNotificationsBarDeleteCalendarGroupFailed,
} from './deleteCalendarGroup.locstring.json';
import loc, { format } from 'owa-localize';

import type { CalendarGroup } from 'owa-graph-schema';
import { ControlIcons } from 'owa-control-icons';
import { createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import deleteCalendarGroupService from '../services/deleteCalendarGroupService';
import { findInArray } from 'owa-calendar-data-utils/lib/findInArray';
import { getCalendarGroups } from 'owa-calendar-surface-calendars-store';
import { getUserMailboxInfo } from 'owa-client-ids';
import { returnTopExecutingActionDatapoint } from 'owa-analytics';
import showCalendarErrorNotification from './showCalendarErrorNotification';
import showUndoNotificationForDelete from '../utils/showUndoNotificationForDelete';
import { trace } from 'owa-trace';
import { tryAgainLabel } from 'owa-locstrings/lib/strings/tryagainlabel.locstring.json';

/**
 * Action to delete calendar group
 */
export let deleteCalendarGroup = createLegacyActionWithDatapoint('DeleteCalendarGroup')(
    async function deleteCalendarGroupInternal(
        userIdentity: string,
        calendarGroup: CalendarGroup
    ): Promise<void> {
        const dataPoint = returnTopExecutingActionDatapoint();

        let groupIndex = instanceDeleteCalendarGroup(
            userIdentity,
            calendarGroup,
            getCalendarGroups()
        );
        if (dataPoint) {
            dataPoint.markUserPerceivedTime(); // mark user perceived time here as complete local lie
        }

        // this notification gives the user a chance to cancel their action before actually sending to the server
        // if shouldUndo returns true, we return back without calling handleCalendarServiceError
        let shouldUndo = await showUndoNotificationForDelete(
            'deleteCalendarGroup',
            format(loc(calendarNotificationsBarDeleteCalendarGroup), calendarGroup.GroupName)
        );
        if (shouldUndo) {
            restoreDeletedCalendarGroup(getCalendarGroups(), groupIndex, calendarGroup);
            return;
        }

        const response = await executeDeleteCalendarGroupRequest(calendarGroup, userIdentity);

        if (!response) {
            onRequestFailed(calendarGroup, groupIndex, userIdentity);
        }
    }
);

async function executeDeleteCalendarGroupRequest(
    calendarGroup: CalendarGroup,
    userIdentity: string
) {
    try {
        await deleteCalendarGroupService(calendarGroup, getUserMailboxInfo(userIdentity));
    } catch (error) {
        return null;
    }
}

function onRequestFailed(calendarGroup: CalendarGroup, groupIndex: number, userIdentity: string) {
    return () => {
        restoreDeletedCalendarGroup(getCalendarGroups(), groupIndex, calendarGroup);

        showCalendarErrorNotification(
            'deleteCalendarGroup',
            format(loc(calendarNotificationsBarDeleteCalendarGroupFailed), calendarGroup.GroupName),
            ControlIcons.Error,
            loc(tryAgainLabel),
            () => deleteCalendarGroup(userIdentity, calendarGroup)
        );
    };
}

function instanceDeleteCalendarGroup(
    userIdentity: string,
    calendarGroup: CalendarGroup,
    calendarGroups: CalendarGroup[]
): number {
    let foundCalendarGroup = findInArray(calendarGroups, group => {
        return (
            group == calendarGroup && group.calendarGroupId.mailboxInfo.userIdentity == userIdentity
        );
    });

    if (foundCalendarGroup) {
        calendarGroups.splice(foundCalendarGroup.index, 1);
        return foundCalendarGroup.index;
    } else {
        trace.warn(`[deleteCalendarGroup] Calendar group is not found: ${calendarGroup.GroupName}`);
        return -1;
    }
}

function restoreDeletedCalendarGroup(
    calendarGroups: CalendarGroup[],
    calendarGroupIndex: number,
    calendarGroupToInsert: CalendarGroup
) {
    if (calendarGroupIndex >= 0) {
        calendarGroups.splice(calendarGroupIndex, 0, calendarGroupToInsert);
    }
}

export default deleteCalendarGroup;
